/**
 * File Access Gateway
 *
 * Unified file-system access layer for the browser runtime.
 *
 * Strategy:
 * - Chromium-based browsers expose the File System Access API, which opens
 *   the OPERATING SYSTEM's native Open/Save dialogs and allows real
 *   read/write access to files on disk (no download round-trips).
 * - Browsers without the API (Firefox, Safari) fall back to the in-app file
 *   browser over the virtual FS (virtualFs.ts) for both reads and writes;
 *   the Blob download in this module remains the last-resort save path.
 */

export const PROJECT_FILE_EXTENSION = 'yap';
export const TEMPLATE_FILE_EXTENSION = 'yate';

/** Accept-list shared by every Open/Save picker in the application. */
export const DOCUMENT_FILE_TYPES = [
    { description: 'Yap Project', accept: { 'application/json': [`.${PROJECT_FILE_EXTENSION}`] } },
    { description: 'Yap Project Template', accept: { 'application/json': [`.${TEMPLATE_FILE_EXTENSION}`] } },
];

/** Minimal local typings for the File System Access API (not in lib.dom). */
interface OpenFilePickerOptions {
    types?: { description: string; accept: Record<string, string[]> }[];
    excludeAcceptAllOption?: boolean;
    multiple?: boolean;
}
interface SaveFilePickerOptions {
    suggestedName?: string;
    types?: { description: string; accept: Record<string, string[]> }[];
}

declare global {
    interface Window {
        showOpenFilePicker?: (options?: OpenFilePickerOptions) => Promise<FileSystemFileHandle[]>;
        showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;
    }
}

/** True when the runtime supports native OS file dialogs with write access. */
export function isFileSystemAccessSupported(): boolean {
    return typeof window !== 'undefined' && typeof window.showOpenFilePicker === 'function';
}

/**
 * Opens the native OS Open dialog and returns the selected file handle,
 * or null when the user cancels.
 */
export async function pickFileToOpen(): Promise<FileSystemFileHandle | null> {
    if (!isFileSystemAccessSupported()) return null;
    try {
        const [handle] = await window.showOpenFilePicker!({
            types: DOCUMENT_FILE_TYPES,
            excludeAcceptAllOption: false,
            multiple: false,
        });
        return handle ?? null;
    } catch (e) {
        if (isAbortError(e)) return null;
        throw e;
    }
}

/**
 * Opens the native OS Save dialog and returns the chosen file handle,
 * or null when the user cancels.
 */
export async function pickFileToSave(suggestedName: string): Promise<FileSystemFileHandle | null> {
    if (!isFileSystemAccessSupported()) return null;
    try {
        return await window.showSaveFilePicker!({
            suggestedName,
            types: DOCUMENT_FILE_TYPES,
        });
    } catch (e) {
        if (isAbortError(e)) return null;
        throw e;
    }
}

/** Reads the full text content of a file handle. */
export async function readHandleText(handle: FileSystemFileHandle): Promise<string> {
    const file = await handle.getFile();
    return file.text();
}

/** Writes (replaces) the full text content of a file handle. */
export async function writeHandleText(handle: FileSystemFileHandle, contents: string): Promise<void> {
    const writable = await handle.createWritable();
    await writable.write(contents);
    await writable.close();
}

/** File name without its extension — used for document display names. */
export function stripExtension(fileName: string): string {
    return fileName.replace(/\.(yap|yate|json)$/i, '');
}

/** Triggers a browser download for the given text content (last-resort save). */
export function downloadTextFile(fileName: string, contents: string, mime = 'application/json'): void {
    const blob = new Blob([contents], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
}
